/**
 * v130 — `syncToServer` stub.
 *
 * The v130 spec says: "Don't ship a server backend yet — local Storage /
 * IndexedDB only for v130, with a `syncToServer` stub."
 *
 * This module is that stub. It defines the contract a future server sync
 * implementation must satisfy:
 *
 *   1. Anonymized aggregates only. The server side never receives PII
 *      (no names, no precise locations, no photos). The shapes here
 *      strip those fields before upload.
 *   2. Versioned payload. Future server-side migrations need to know the
 *      schema the client is on.
 *   3. Per-session opt-in. The user can flag a session as private —
 *      private sessions never sync.
 *   4. The contract aims at the long-term goal: a quarterly Methods-page
 *      data cut showing the actual archery population's tune behavior.
 *      Treat the shape like a forever-API.
 *
 * Until the server lands, `syncToServer` is a no-op that resolves immediately.
 * It logs to console in dev so the integration plumbing can be tested locally.
 */

import type { ShotSession } from './types';

/** Schema version of the upload payload. Bump on shape changes. */
const SYNC_PAYLOAD_VERSION = 1 as const;

export interface AnonymizedSessionPayload {
  /** Schema version. */
  v: typeof SYNC_PAYLOAD_VERSION;
  /** Stable random client ID — generated on first session, persists locally. */
  clientId: string;
  /** Session UUID (already anonymized — not tied to a user). */
  sessionId: string;
  /** ISO timestamp. */
  loggedAt: string;
  /** Distance + arrow count + group size (legacy / v1 quick log). */
  distanceYd: number;
  arrowCount: number;
  groupSizeIn: number;
  pattern: string;
  /** v2 aggregates IF the session has rich shots. */
  shotCount?: number;
  meanRadiusIn?: number;
  group95RadiusIn?: number;
  /** Bow-class context. NOT brand/model — too identifying for v1. */
  bowCategory?: 'hunting' | 'target' | 'trad';
  /** Coarse geography only — country code at most. */
  countryCode?: string;
  /** Coarse atmospherics — bucketed. */
  daBucketFt?: 0 | 2500 | 5000 | 7500 | 10000;
  windBucketMph?: 0 | 5 | 10 | 15;
}

/**
 * Build the anonymized payload from a session. Strips PII; buckets atmospherics.
 */
export function buildSyncPayload(
  session: ShotSession,
  clientId: string,
): AnonymizedSessionPayload {
  const payload: AnonymizedSessionPayload = {
    v: SYNC_PAYLOAD_VERSION,
    clientId,
    sessionId: session.id,
    loggedAt: session.loggedAt,
    distanceYd: session.distanceYd,
    arrowCount: session.arrowCount,
    groupSizeIn: session.groupSizeIn,
    pattern: session.pattern,
  };
  // v2 rich-shot extensions, if present
  // (using duck-typing because the v2 fields are optional extensions
  // declared in v2-schema.ts to keep this module dependency-light)
  const v2 = session as ShotSession & {
    richShots?: Array<{ hitXIn: number; hitYIn: number }>;
    conditions?: { densityAltitudeFt?: number; windMph?: number };
  };
  if (v2.richShots && v2.richShots.length > 0) {
    payload.shotCount = v2.richShots.length;
    const radii = v2.richShots.map((s) =>
      Math.sqrt(s.hitXIn ** 2 + s.hitYIn ** 2),
    );
    payload.meanRadiusIn = radii.reduce((a, b) => a + b, 0) / radii.length;
  }
  if (v2.conditions) {
    const da = v2.conditions.densityAltitudeFt;
    if (typeof da === 'number') {
      payload.daBucketFt =
        da < 1250 ? 0
        : da < 3750 ? 2500
        : da < 6250 ? 5000
        : da < 8750 ? 7500
        : 10000;
    }
    const w = v2.conditions.windMph;
    if (typeof w === 'number') {
      payload.windBucketMph =
        w < 2.5 ? 0
        : w < 7.5 ? 5
        : w < 12.5 ? 10
        : 15;
    }
  }
  return payload;
}

/**
 * Best-effort, no-op-by-default upload. When the server endpoint exists,
 * this resolves into a real POST. Until then, it console-logs in dev so
 * integration plumbing can be tested locally.
 *
 * Network errors are swallowed deliberately — sync is best-effort. The
 * local log is authoritative.
 */
export async function syncToServer(
  payload: AnonymizedSessionPayload,
): Promise<{ ok: boolean; reason?: string }> {
  if (typeof window === 'undefined') {
    return { ok: false, reason: 'no-window' };
  }
  // No endpoint configured — return ok:false with a clear reason so the
  // call site doesn't surface a confusing error to the user.
  // When the server lands, replace this with the real fetch.
  if (import.meta.env?.DEV) {
    // eslint-disable-next-line no-console
    console.log('[v130 syncToServer · stub]', payload);
  }
  return { ok: false, reason: 'sync-not-yet-enabled' };
}

/**
 * Lazy-initialize a stable client ID for sync purposes. Stored in localStorage
 * under its own key so it survives Range log resets.
 */
export function getOrCreateClientId(): string {
  const KEY = 'sparrow.rangeLog.clientId';
  try {
    const existing = window.localStorage.getItem(KEY);
    if (existing) return existing;
    const id = `c_${crypto.randomUUID?.() ?? Math.random().toString(36).slice(2)}`;
    window.localStorage.setItem(KEY, id);
    return id;
  } catch {
    return `c_ephemeral_${Math.random().toString(36).slice(2, 10)}`;
  }
}
